import { AccessLevel, AccessToken, AccessTokenPayload, AccessTokenType, ProductType } from '@dev/ee-api/ee-api-models';
import {
    AccessTokenAddError,
    AccessTokenAddSuccess,
    AccessTokenDeleteError,
    AccessTokenDeleteSuccess,
    AccessTokenUpdateError,
    AccessTokenUpdateSuccess,
    AccessTokensLoadingError,
    AccessTokensLoadingSuccess,
    ChangeAccessTokenError,
    ChangeAccessTokenSuccess,
    ShowAccessTokenCredentials,
} from '@api/store/security/actions';
import {
    AccessTokenDialogTypeEnum,
    AddAccessTokenAction,
    ChangeAccessTokenAction,
    DeleteAccessTokenAction,
    SecurityState,
    UpdateAccessTokenAction,
} from '@api/store/security/types';
import { call, fork, put, select, takeEvery } from 'redux-saga/effects';
import { changeAccessToken, createAccessToken, deleteAccessToken, loadAccessTokens, updateAccessToken } from '@api/services/ElasticApi';

import { ShowToast } from '@api/store/toast/actions';
import { ApplicationState as State } from '@api/store';
import { errorToast } from './commonGenerators/errorToast';
import i18n from '@api/i18n';
import { sendEventToGTM } from '@dev/ee-tools';
import { Logger } from '@api/services/errors/appLogger';

const getAccessTokens = (state: State): SecurityState => state.security;

function* fetchAccessTokens(): any {
    try {
        const loadedAccessTokens = yield call(loadAccessTokens);

        yield put(AccessTokensLoadingSuccess(loadedAccessTokens.data));
    } catch (e) {
        Logger.error(e.response.data.Error);
        yield put(AccessTokensLoadingError());
        yield put(
            ShowToast({
                message: 'Error: ' + e.response.data.Error,
                severity: 'error',
                duration: 3000,
            })
        );
    }
}

function* addAccessToken(action: AddAccessTokenAction): any {
    const { payload } = action;
    const newValues: AccessTokenPayload = {
        Expires: null,
        RestrictAccessToIPRange: [],
        AccessLevel: [AccessLevel.None],
        ...payload.accessToken,
    };

    try {
        const newAccessToken = yield call(createAccessToken, newValues);
        const userAccessTokens = yield select(getAccessTokens);
        const { accessTokens } = userAccessTokens;

        const { Token, ...newAccessTokenParams } = newAccessToken.data;

        yield put(AccessTokenAddSuccess([...accessTokens, newAccessTokenParams]));
        yield put(
            ShowAccessTokenCredentials(newAccessTokenParams.Name, Token, AccessTokenDialogTypeEnum.EditToken, newAccessTokenParams.Type)
        );
        sendEventToGTM('AddAccessToken', { type: newAccessTokenParams?.Type, productType: ProductType.API });
    } catch (e) {
        yield put(AccessTokenAddError());
        yield call(errorToast, e);
    }
}

function* removeAccessToken(action: DeleteAccessTokenAction): any {
    const { payload } = action;
    const userAccessTokens = yield select(getAccessTokens);
    const { accessTokens } = userAccessTokens;
    const tokenToRemove: any = {
        name: payload.name,
        type: payload.type,
    };
    try {
        const deleteToken = yield call(deleteAccessToken, tokenToRemove); //eslint-disable-line
        yield put(AccessTokenDeleteSuccess());
        const remainingTokens = accessTokens.filter((token) => !(token.Name === payload.name && token.Type === payload.type));
        yield put(AccessTokenUpdateSuccess(remainingTokens));
        yield put(
            ShowToast({
                message: i18n.t('itemdeleted', {
                    item: payload.type === AccessTokenType.SMTPCredential ? i18n.t('credential') : i18n.t('apikey'),
                }),
                severity: 'info',
                duration: 3000,
            })
        );
    } catch (e) {
        Logger.error(e.response.data.Error);
        yield put(AccessTokenDeleteError());
        yield put(
            ShowToast({
                message: 'Error: ' + e.response.data.Error,
                severity: 'error',
                duration: 3000,
            })
        );
    }
}

function* updateAccessTokenParams(action: UpdateAccessTokenAction): any {
    const { payload } = action;
    const userAccessTokens = yield select(getAccessTokens);

    const selectedToken = userAccessTokens.accessTokens.find(
        (el): boolean => el.Name === payload.name && el.Type === payload?.accessToken.Type
    );

    const { AccessLevel: accessLevelsToAdd, ...restParams } = payload?.accessToken;

    const updatedTokenParams: AccessTokenPayload = {
        AccessLevel: accessLevelsToAdd,
        TokenName: selectedToken.Name,
        RestrictAccessToIPRange: selectedToken.RestrictAccessToIPRange,
        Expires: selectedToken.Expires,
        Type: selectedToken.Type,
        ...restParams,
    };
    const params = {
        name: payload.name,
        body: updatedTokenParams,
    };
    try {
        const updateCall = yield call(updateAccessToken, params);
        const updatedList = userAccessTokens?.accessTokens?.map((el): AccessToken => {
            if (el.Name === payload?.name && el.Type === payload?.accessToken.Type) {
                return updateCall?.data;
            }
            return el;
        });

        yield put(AccessTokenUpdateSuccess(updatedList));

        yield put(
            ShowToast({
                message: i18n.t('changesaved'),
                severity: 'info',
                duration: 3000,
            })
        );
    } catch (e) {
        yield put(AccessTokenUpdateError());
        yield call(errorToast, e);
    }
}
function* patchAccessToken(action: ChangeAccessTokenAction): any {
    const { payload } = action;
    try {
        const changeToken = yield call(changeAccessToken, payload.name, payload.type);
        yield put(ChangeAccessTokenSuccess());
        yield put(ShowAccessTokenCredentials(payload.name, changeToken.data, AccessTokenDialogTypeEnum.ChangePassword));
    } catch (e) {
        Logger.error(e.response.data.Error);
        yield put(ChangeAccessTokenError());
        yield put(
            ShowToast({
                message: 'Error: ' + e.response.data.Error,
                severity: 'error',
                duration: 3000,
            })
        );
    }
}

function* watchForAccessTokenFetch(): any {
    yield takeEvery('@@security/FETCH_ACCESS_TOKENS', fetchAccessTokens);
}
function* watchForAccessTokenAdd(): any {
    yield takeEvery('@@security/ADD_ACCESS_TOKEN', addAccessToken);
}
function* watchForAccessTokenDelete(): any {
    yield takeEvery('@@security/DELETE_ACCESS_TOKEN', removeAccessToken);
}
function* watchForAccessTokenUpdate(): any {
    yield takeEvery('@@security/UPDATE_ACCESS_TOKEN', updateAccessTokenParams);
}
function* watchForAccessTokenChange(): any {
    yield takeEvery('@@security/CHANGE_ACCESS_TOKEN', patchAccessToken);
}

const SecuritySagas = [
    fork(watchForAccessTokenFetch),
    fork(watchForAccessTokenAdd),
    fork(watchForAccessTokenDelete),
    fork(watchForAccessTokenUpdate),
    fork(watchForAccessTokenChange),
];

export default SecuritySagas;
